import { useEffect, useRef, useState } from 'react';
import type { VavClient, UserSummary } from '@vav/client-core';
import { Avatar } from './Avatar.tsx';
import { useT } from '../i18n.tsx';

interface Props {
  client: VavClient;
  onClose: () => void;
  onOpened: (conversationId: string) => void;
}

export function NewChatDialog({ client, onClose, onOpened }: Props) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSummary[]>([]);
  const [selected, setSelected] = useState<UserSummary[]>([]);
  const [groupName, setGroupName] = useState('');
  const [groupMode, setGroupMode] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string>();
  const dialogRef = useRef<HTMLDivElement>(null);

  // Debounced search: a request per keystroke would hammer the directory
  // endpoint and land results out of order.
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      client
        .searchUsers(term)
        .then(setResults)
        .catch((err: Error) => setError(err.message))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, client]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function toggle(user: UserSummary) {
    setSelected((prev) => {
      if (prev.some((u) => u.id === user.id)) return prev.filter((u) => u.id !== user.id);
      // A direct chat has exactly one other person; picking a second should
      // replace the first rather than silently turning it into a group.
      return groupMode ? [...prev, user] : [user];
    });
  }

  async function start() {
    setError(undefined);
    try {
      if (!groupMode) {
        const conversation = await client.startDirectConversation(selected[0]);
        onOpened(conversation.id);
        return;
      }
      const name = groupName.trim() || selected.map((u) => u.displayName).join(', ');
      const conversation = await client.createGroup(
        name,
        selected.map((u) => u.id),
      );
      onOpened(conversation.id);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const isGroup = groupMode;

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" ref={dialogRef} role="dialog" aria-modal="true" aria-label={t('newChat.title')}>
        <header className="dialog__header">
          <h2>{t('newChat.title')}</h2>
          <button className="icon" onClick={onClose} aria-label={t('invite.close')}>
            ✕
          </button>
        </header>

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('newChat.search')}
          autoFocus
          dir="auto"
        />

        {/*
          Groups were only reachable by selecting a second person, which nobody
          discovers on their own. This makes the mode explicit up front.
        */}
        <div className="modeToggle" role="tablist">
          <button
            role="tab"
            aria-selected={!groupMode}
            className={!groupMode ? 'is-active' : ''}
            onClick={() => setGroupMode(false)}
          >
            💬 {t('newChat.direct')}
          </button>
          <button
            role="tab"
            aria-selected={groupMode}
            className={groupMode ? 'is-active' : ''}
            onClick={() => setGroupMode(true)}
          >
            👥 {t('newChat.group')}
          </button>
        </div>

        {selected.length > 0 && (
          <div className="chips">
            {selected.map((user) => (
              <button key={user.id} className="chip" onClick={() => toggle(user)}>
                {user.displayName} ✕
              </button>
            ))}
          </div>
        )}

        {groupMode && (
          <input
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder={t('newChat.groupName')}
            dir="auto"
          />
        )}
        {groupMode && (
          <p className="hint">{t('newChat.pickHint')}</p>
        )}

        <div className="results">
          {searching && <p className="muted">{t('newChat.searching')}</p>}
          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <p className="muted">{t('newChat.noResults')}</p>
          )}
          {results.map((user) => {
            const picked = selected.some((u) => u.id === user.id);
            return (
              <button
                key={user.id}
                className={`result ${picked ? 'is-picked' : ''}`}
                onClick={() => toggle(user)}
              >
                <Avatar name={user.displayName} size={38} />
                <span>
                  <strong>{user.displayName}</strong>
                  <small className="muted">@{user.username}</small>
                </span>
                {picked && <span className="check">✓</span>}
              </button>
            );
          })}
        </div>

        {error && <p className="error">{error}</p>}

        <button
          className="primary"
          disabled={selected.length === 0 || (groupMode && selected.length < 2)}
          onClick={start}
        >
          {isGroup ? t('newChat.createGroup') : t('newChat.openChat')}
        </button>
      </div>
    </div>
  );
}
